// https://www.radix-ui.com/primitives/docs/components/popover
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useState, type ReactNode } from "react";
import cx from "ui/cx";
import { PopoverContainerContext, usePopoverContainer } from "ui/hooks/usePopoverContainer";
import IconClose from "ui/icons/IconClose";
import styles from "./Popover.module.scss";

interface PopoverRootProps {
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
}

interface PopoverTriggerProps {
  children: ReactNode;
  asChild?: boolean;
  className?: string;
}

interface PopoverContentProps {
  children: ReactNode;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  alignOffset?: number;
  avoidCollisions?: boolean;
  collisionBoundary?: Element | null | Array<Element | null>;
  collisionPadding?: number | Partial<Record<"top" | "right" | "bottom" | "left", number>>;
  arrowPadding?: number;
  sticky?: "partial" | "always";
  hideWhenDetached?: boolean;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: CustomEvent<{ originalEvent: PointerEvent }>) => void;
  onFocusOutside?: (event: CustomEvent<{ originalEvent: FocusEvent }>) => void;
  onInteractOutside?: (event: CustomEvent<{ originalEvent: PointerEvent | FocusEvent }>) => void;
  forceMount?: true;
  // Portal props
  container?: HTMLElement;
}

interface PopoverArrowProps {
  className?: string;
  width?: number;
  height?: number;
  asChild?: boolean;
}

interface PopoverCloseProps {
  children?: ReactNode;
  asChild?: boolean;
  className?: string;
}

interface PopoverAnchorProps {
  children: ReactNode;
  asChild?: boolean;
  className?: string;
}

function PopoverRoot({ children, ...props }: PopoverRootProps) {
  return <PopoverPrimitive.Root {...props}>{children}</PopoverPrimitive.Root>;
}

function PopoverTrigger({ children, className, ...props }: PopoverTriggerProps) {
  return (
    <PopoverPrimitive.Trigger className={cx(className)} data-testid="popover-trigger" {...props}>
      {children}
    </PopoverPrimitive.Trigger>
  );
}

function PopoverContent({
  children,
  className,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  collisionPadding = 24,
  hideWhenDetached = true,
  container,
  ...props
}: PopoverContentProps) {
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const basePopoverContainer = usePopoverContainer();
  const popoverContainer = container ?? basePopoverContainer;
  return (
    <PopoverPrimitive.Portal container={popoverContainer}>
      <PopoverContainerContext.Provider value={contentElement}>
        <PopoverPrimitive.Content
          ref={setContentElement}
          className={cx(styles.content, className)}
          side={side}
          sideOffset={sideOffset}
          align={align}
          collisionPadding={collisionPadding}
          data-testid="popover-content"
          hideWhenDetached={hideWhenDetached}
          onOpenAutoFocus={(event) => {
            // Prevents triggering tooltip of item on open
            event.preventDefault();
          }}
          {...props}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverContainerContext.Provider>
    </PopoverPrimitive.Portal>
  );
}

function PopoverArrow({ className, width = 10, height = 5, ...props }: PopoverArrowProps) {
  return (
    <PopoverPrimitive.Arrow
      className={cx(styles.arrow, className)}
      width={width}
      height={height}
      data-testid="popover-arrow"
      {...props}
    />
  );
}

function PopoverClose({ children, className, ...props }: PopoverCloseProps) {
  return (
    <PopoverPrimitive.Close className={cx(styles.close, className)} data-testid="popover-close" {...props}>
      {children || <IconClose />}
    </PopoverPrimitive.Close>
  );
}

function PopoverAnchor({ children, className, ...props }: PopoverAnchorProps) {
  return (
    <PopoverPrimitive.Anchor className={cx(className)} {...props}>
      {children}
    </PopoverPrimitive.Anchor>
  );
}

// Compound component exports
export const Popover = {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Content: PopoverContent,
  Arrow: PopoverArrow,
  Close: PopoverClose,
  Anchor: PopoverAnchor,
};

// Individual component exports for flexibility
export { PopoverRoot, PopoverTrigger, PopoverContent, PopoverArrow, PopoverClose, PopoverAnchor };

// Type exports
export type {
  PopoverRootProps,
  PopoverTriggerProps,
  PopoverContentProps,
  PopoverArrowProps,
  PopoverCloseProps,
  PopoverAnchorProps,
};

export default Popover;
